import Bluebird from "bluebird";
import { produce } from "immer";
import { useContext } from "react";
import uuid from "uuid/v4";

import {
  CreateExamConfigData,
  schemaParams,
  timestampParams,
} from "@examspark/api";
import {
  filterMaybeArray,
  formatClassroomActivityKey,
  generateExamTicketCode,
} from "@examspark/common";
import {
  assertNativeError,
  getDateTimestamp,
} from "@examspark/common/dist/utils";
import {
  ExamConfigData,
  MakeOptional,
  ParsedClassroom,
  Resource,
  ResourceMaturity,
  ResourceVisibilityType,
  SearchableResourceFilterInput,
  Student,
  Unit,
} from "@examspark/types";

import {
  useActivityBuilderSearch,
  useMutationCreateClassroomActivity,
  useMutationCreateClassroomActivityTicket,
  useMutationCreateExam,
  useMutationCreateExamConfig,
  useMutationListClassroomStudents,
} from "../../../../api";
import { NewActivityContext, NewActivityState } from "../../../../classroom";
import { useGoogleClassroom } from "../../../../hooks/useGoogleClassroom";
import { useGoogleSignIn } from "../../../../hooks/useGoogleSignIn";
import { useAppContext } from "../../../../providers/AppContext";

import { defaultExamConfigData } from "./useActivityEditor";
import { getFilterForNewActivity } from "./useSearch";

export const defaultNewActivityState: NewActivityState = {
  name: "",
  description: "",
  questions: [],
  banks: [],
  config: {},
  interactionTypes: [],
  passages: [],
  objectives: [],
  passagesAvailableCount: 0,
};

export const useNewActivity = () => {
  const [context] = useAppContext();
  const [state, dispatch] = useContext(NewActivityContext);
  const activitySearch = useActivityBuilderSearch();
  const gc = useGoogleClassroom({
    tokenServerUrl:
      process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
  });
  const gsi = useGoogleSignIn({
    tokenServerUrl:
      process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
  });

  const createExamConfigMutation = useMutationCreateExamConfig();
  const createActivityMutation = useMutationCreateExam();
  const createClassroomActivityMutation = useMutationCreateClassroomActivity();
  const listClassroomStudents = useMutationListClassroomStudents();
  const createClassroomActivityTicket =
    useMutationCreateClassroomActivityTicket();

  const buildActivity = async () => {
    const questions: any[] = [];
    const bankIds = state.banks;

    // initialize an empty activity if they selected no objectives
    if (!state.objectives?.length) {
      dispatch({
        type: "UPDATE",
        data: produce(state, (draft) => {
          draft.questions = questions;
          draft.isInitialized = true;
        }),
      });
      return;
    }

    let filterBankIds = {};
    if (bankIds) {
      filterBankIds = {
        or: bankIds?.map((bankId) => ({
          bankId: { eq: bankId },
        })),
      };
    }

    await Promise.all(
      state.objectives.map(async (objective) => {
        const filter: SearchableResourceFilterInput = {
          alignmentIds: {
            eq: objective.id,
          },
          sk: {
            eq: "item#v0",
          },
          isDeleted: {
            ne: true,
          },
          ...filterBankIds,
          ...getFilterForNewActivity({
            filter: {
              interactionTypes: state.interactionTypes,
              contextIds: state.passages.map((p) => p.id),
              language: state.language,
            },
          }),
          // the builder can specify additional filters
          // on each objective (currently unit, potentially passages, etc.)
          ...objective.additionalFilter,
        };

        const searchResult = await activitySearch.mutateAsync({
          limit: 20,
          filter,
          authorizationContext: {
            workspaceId: context.workspaceUser?.workspace?.id,
            appId: context.app?.id,
            userId: context.user?.id,
          },
        });

        if (!searchResult?.items?.length) return;
        let pushed_i = 0;
        for (const result of searchResult?.items ?? []) {
          pushed_i++;
          if (objective.qty != null && pushed_i > Number(objective.qty)) break;
          if (questions.find((q) => q.id === result.id)) return;
          questions.push(result);
        }
      }),
    );

    // if no items are found AND they tried (selected objectives), throw an error
    if (questions.length === 0) {
      throw new Error("No items found");
    }

    dispatch({
      type: "UPDATE",
      data: produce(state, (draft) => {
        draft.questions = questions;
        draft.isInitialized = true;
      }),
    });

    return questions;
  };

  // create JUST the exam record, such as for a to-be-administered district assessment
  const createDistrictExam = async (workspaceId: string, userId: string) => {
    const examData = getExamDataForCreate({
      data: {
        name: state.name || `New Assessment ${getDateTimestamp()}`,
        description: state.description,
        subject: [state.subject ?? ""],
        bankId: `${workspaceId}/user-content`,
        maturity: ResourceMaturity.Operational,
        visibility: ResourceVisibilityType.Private,
        interaction: state.interactionTypes,
        creatorId: userId,
      },
      itemIds: state.questions.map((item) => item.id),
      workspaceId,
      unitIds: state.units?.map((u) => u.id),
      courseIds: state.courses?.map((c) => c.id),
    });

    try {
      await createActivityMutation.mutateAsync(examData);

      return {
        id: examData.id,
        sk: "exam#v0",
      };
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
    }
  };

  /**
   * Creates:
   * - exam
   * - examConfig
   * - classroomActivity
   * - examTickets for each student in the classroom
   *
   * ...such that we are immedately ready to administer in a bonfire classroom
   *
   * @deprecated Sort of deprecated for `useMutationAddExamToClassroom` hook.
   * Lots of similar code, should look into consolidating.
   */
  const createActivity = async (
    workspaceId: string,
    userId: string,
    appKeyId: string,
    appId: string,
    classroom: ParsedClassroom,
    unit: Unit,
  ) => {
    const name = state.name || `${unit?.name} ${getDateTimestamp()}`;
    const gcCourse = classroom.data?.googleClassroom?.course;
    if (!workspaceId) {
      console.error("No workspace user id found. Exiting build.");
      return;
    }

    try {
      const subject = unit.subject?.[0];
      if (!subject) throw new Error("No subject set for this unit.");

      const examConfigId = uuid();
      let coursework: gapi.client.classroom.CourseWork | undefined;

      // If we're creating for google classroom, we need to ensure the activity is
      // successfully created there first
      if (classroom.data?.platform === "GOOGLE_CLASSROOM" && !!gcCourse) {
        coursework = await assignToGoogleClassroomCourse(classroom, {
          id: examConfigId,
          name: name,
          description: state.description,
        });

        if (!coursework) {
          throw new Error("Failed to create coursework in Google Classroom");
        }
      }

      // create activity
      const itemIds = Array.from(
        new Set<string>(state.questions.map((item) => item.id)),
      );

      const commonData = {
        name: name,
        description: state.description,
        subject: [subject],
        creatorId: userId,
      };

      // create `exam`
      const createExamResult = await createActivityMutation.mutateAsync(
        getExamDataForCreate({
          data: {
            ...commonData,
            bankId: `${workspaceId}/user-content`,
            maturity: ResourceMaturity.Operational,
            visibility: ResourceVisibilityType.Private,
            interaction: state.interactionTypes,
          },
          itemIds,
          workspaceId,
          unitIds: state.units?.map((u) => u.id),
          courseIds: state.courses?.map((c) => c.id),
        }),
      );

      if (!createExamResult) throw new Error("No exam found");

      // create `exam-config`
      const examId = createExamResult.id;
      const createExamConfigResult = await createExamConfigMutation.mutateAsync(
        {
          input: getExamConfigDataForCreate({
            classroom,
            gcCoursework: coursework,
            examConfigData: {
              ...commonData,
              workspaceId: workspaceId,
              maturity: "IN_PROGRESS",
              resourceId: [examId, "exam#v0"].join("/"),
              appId: appId,
              appKey: appKeyId,
              alignmentIds: state.objectives.map((o) => o.id),
            },
          }),
          options: {
            createAccessCode: true,
          },
        },
      );

      if (!createExamConfigResult) throw new Error("No examConfig found");

      const createClassroomActivityResult =
        await createClassroomActivityMutation.mutateAsync(
          getClassroomActivityDataForCreate({
            classroom,
            unitId: unit.id,
            workspaceId: workspaceId,
            creatorId: userId,
            examConfigId,
          }),
        );

      // create student-specific exam tickets
      const students = await listClassroomStudents.mutateAsync({
        workspaceId: workspaceId,
        classroomId: classroom.id,
      });

      Bluebird.map(
        filterMaybeArray(students ?? []),
        async (student) => {
          return await createExamTicketForStudent({
            workspaceId,
            userId,
            examConfigId,
            student,
          });
        },
        {
          concurrency: 5,
        },
      );

      console.log(
        "create activity result",
        createExamResult,
        createExamConfigResult,
        createClassroomActivityResult,
      );

      return {
        id: examConfigId,
        sk: "exam-config",
      };
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
    }
  };

  const createExamTicketForStudent = async (params: {
    workspaceId: string;
    userId: string;
    examConfigId: string;
    student: Student;
  }) => {
    const { workspaceId, userId, examConfigId, student } = params;
    const [, studentId] = student.sk.split("#");
    const ticketId = generateExamTicketCode();

    console.log("ticketId", ticketId);
    await createClassroomActivityTicket.mutateAsync({
      id: ticketId,
      sk: `exam-ticket`,
      schemaVersion: "2020-01-01",
      workspaceId: workspaceId,
      creatorId: userId,
      resourceId: [examConfigId, "exam-config"].join("/"),
      tags: [
        {
          key: "student-id",
          type: "string",
          value: studentId,
        },
      ],
    });

    // create exam-ticket#[examConfigId] for listing purposes
    await createClassroomActivityTicket.mutateAsync({
      id: ticketId,
      sk: `exam-ticket#${examConfigId}`,
      schemaVersion: "2020-01-01",
      workspaceId: workspaceId,
      creatorId: userId,
      resourceId: [examConfigId, "exam-config"].join("/"),
      tags: [
        {
          key: "student-id",
          type: "string",
          value: studentId,
        },
      ],
    });
  };

  const assignToGoogleClassroomCourse = async (
    classroom: ParsedClassroom,
    examConfig: Pick<Resource, "name" | "description" | "id">,
  ) => {
    try {
      const refreshToken = classroom.data?.googleClassroom.auth.refreshToken;
      const courseId = classroom.data?.googleClassroom.course.id;

      if (!refreshToken) throw new Error("no refresh token");
      if (!courseId) throw new Error("no refresh token");
      const accessToken = await gsi.refreshAccessToken(refreshToken);
      if (!accessToken) throw new Error("no access token");
      gc.setToken(accessToken.access_token);

      const coursework = await gc.createCourseWork(courseId, {
        title: examConfig.name,
        description: examConfig.description,
        examConfigId: examConfig.id,
      });

      console.log("coursework", coursework);
      return coursework;
    } catch (error) {
      assertNativeError(error);
      console.error(error);
    }
  };

  return { buildActivity, createActivity, createDistrictExam };
  // return [state, dispatchState];
};

export const getExamDataForCreate = (params: {
  data?: MakeOptional<Resource, "id" | "sk" | "schemaVersion">;
  itemIds: string[];
  workspaceId: string;
  unitIds?: string[];
  courseIds?: string[];
}): Resource => {
  const { workspaceId, unitIds, courseIds, itemIds } = params;

  const examId = uuid();

  const data = {
    id: examId,
    sk: "exam#v0",
    schemaVersion: "2020-01-01",
    name: params.data?.name || `New Assessment ${getDateTimestamp()}`,
    workspaceId: workspaceId,
    bankId: `${workspaceId}/user-content`,
    maturity: ResourceMaturity.Operational,
    visibility: ResourceVisibilityType.Private,
    content: {
      forms: [
        {
          sections: [
            {
              questionIds: itemIds,
            },
          ],
        },
      ],
    },
    questionIds: itemIds,
    ...params.data,
  };

  // todo: move these to something like `additionalTags`
  // instead of having course and unit in state
  const tags = [];

  for (const unitId of unitIds ?? []) {
    tags.push({
      key: "unit-id",
      type: "string",
      value: unitId,
    });
  }

  for (const courseId of courseIds ?? []) {
    tags.push({
      key: "course-id",
      type: "string",
      value: courseId,
    });
  }

  data.tags = tags;
  return data;
};

export const getExamConfigDataForCreate = (params: {
  examConfigData: MakeOptional<CreateExamConfigData, "id" | "tags">;
  classroom?: ParsedClassroom;
  gcCoursework?: gapi.client.classroom.CourseWork;
  unit?: Unit;
}): CreateExamConfigData => {
  const { classroom, gcCoursework, unit, examConfigData } = params;
  let examConfigConfig: ExamConfigData | undefined = {
    ...defaultExamConfigData,
  };

  // in case we need to calculate the examConfigId before we create it
  // ex: if we need to create a google classroom coursework first
  const examConfigId = params.examConfigData.id ?? uuid();

  // optional: handle google classroom coursework creation
  if (classroom?.data?.platform === "GOOGLE_CLASSROOM" && gcCoursework) {
    const gcCourse = classroom.data?.googleClassroom?.course;
    const refreshToken = classroom.data?.googleClassroom.auth.refreshToken;
    examConfigConfig = {
      ...defaultExamConfigData,
      googleClassroomAssignments: [
        {
          /**
           * @todo Temporary way to get refresh token over to `<LaunchExam />` we
           * want to refactor this before we go live. */
          refreshToken: refreshToken,
          courseWorkId: gcCoursework.id ?? "",
          course: {
            id: gcCourse?.id ?? "",
            name: gcCourse?.name ?? "",
            section: gcCourse?.section ?? "",
            room: gcCourse?.room ?? "",
          },
        },
      ],
    };
  }

  const data: CreateExamConfigData = {
    id: examConfigId,
    // sk: "exam-config",
    config: JSON.stringify(examConfigConfig),
    ...schemaParams(),
    ...timestampParams(),
    ...examConfigData,
    tags: [
      {
        key: "cohort-school",
        type: "string",
        value: "bonfire-school-id",
      },
    ],
  };

  if (!data.tags) data.tags = [];
  if (classroom) {
    data.tags.push({
      key: "classroom-id",
      type: "string",
      value: classroom.id,
    });
    data.tags.push({
      key: "cohort-classroom",
      type: "string",
      value: classroom.id,
    });
  }

  if (unit) {
    data.tags.push({
      key: "unit-id",
      type: "string",
      value: unit.id,
    });
  }

  return data;
};

const getClassroomActivityDataForCreate = (params: {
  classroom: ParsedClassroom;
  unitId?: string;
  workspaceId: string;
  creatorId: string;
  examConfigId: string;
}) => {
  const { classroom, unitId, workspaceId, creatorId, examConfigId } = params;

  return {
    ...formatClassroomActivityKey(classroom.id, examConfigId, unitId),
    schemaVersion: "2020-01-01",
    workspaceId: workspaceId,
    creatorId,
    resourceId: [examConfigId, "exam-config"].join("/"),
  };
};
